import React, { useState, useEffect } from "react";
import { firstValueFrom } from "rxjs";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../ui/dialog";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../ui/select";
import { Switch } from "../../../ui/switch";
import { ApplicationService } from "../../../../services/ApplicationService";
import { CompanyService } from "../../../../services/CompanyService";
import { ConversationBuilderService } from "../../../../services/ConversationBuilderService";
import { usePermissions } from "../../../../PermissionsProvider";

interface BackendSaveMetadata {
  conversation_flow_name: string;
  company_id: string;
  application_ref_name: string;
  action_type: string;
  conversation_flow_version: number; // Always 1 for new trees
  is_latest: boolean; // Always true for new trees
}

interface SaveTreeModalProps {
  visible: boolean;
  onSave: (metadata: BackendSaveMetadata) => Promise<void>; // Now async!
  onCancel: () => void;
}

interface Application {
  id: number;
  application_name: string;
  application_ref_name?: string;
}

interface Intent {
  id: number;
  action_type: string;
  description?: string;
}

const SaveTreeModal: React.FC<SaveTreeModalProps> = ({
  visible,
  onSave,
  onCancel,
}) => {
  const { apiKey, companyId } = usePermissions();
  
  // Form state
  const [conversationFlowName, setConversationFlowName] = useState("");
  const [selectedApplicationId, setSelectedApplicationId] = useState<number | null>(null);
  const [selectedIntentId, setSelectedIntentId] = useState<number | null>(null);
  
  // Application management
  const [applications, setApplications] = useState<Application[]>([]);
  const [createNewApplication, setCreateNewApplication] = useState(false);
  const [newApplicationName, setNewApplicationName] = useState("");
  
  // Intent management
  const [intents, setIntents] = useState<Intent[]>([]);
  const [loadingIntents, setLoadingIntents] = useState(false);
  const [createNewIntent, setCreateNewIntent] = useState(false);
  const [newIntentName, setNewIntentName] = useState("");
  const [newIntentDescription, setNewIntentDescription] = useState("");

  // Loading states
  const [isValidating, setIsValidating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Load applications on mount
  useEffect(() => {
    if (companyId && apiKey && visible) {
      const subscription = CompanyService.getApplicationsByCompany(companyId, null, apiKey).subscribe({
        next: (applications: Application[]) => {
          setApplications(applications || []);
        },
        error: (error: any) => {
          console.error("Error loading applications:", error);
          toast.error("Failed to load applications");
        }
      });

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [companyId, apiKey, visible]);

  // Generate application ref name when user blurs the application name field


  // Load intents when application is selected
  useEffect(() => {
    if (selectedApplicationId && apiKey && !createNewApplication) {
      setLoadingIntents(true);
      const subscription = ApplicationService.getApplicationActions(selectedApplicationId, null, apiKey).subscribe({
        next: (intents: Intent[]) => {
          setIntents(intents || []);
          setLoadingIntents(false);
        },
        error: (error: any) => {
          console.error("Error loading intents:", error);
          setIntents([]);
          setLoadingIntents(false);
        }
      });

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [selectedApplicationId, companyId, apiKey, createNewApplication]);

  const handleApplicationChange = (applicationId: string) => {
    const appId = parseInt(applicationId);
    setSelectedApplicationId(appId);
    setSelectedIntentId(undefined); // Reset intent selection
    // Preserve createNewIntent state - don't reset it when changing applications
  };

  const handleOk = async () => {
    if (!conversationFlowName.trim()) {
      toast.error("Please provide a tree name");
      return;
    }

    try {
      setIsValidating(true);
      
      let applicationRefName = "";
      let actionType = "";
      let applicationId: number | null = null;
      let intentId: number | null = null;

      // Step 1: Handle application creation or selection
      if (createNewApplication) {
        if (!newApplicationName.trim()) {
          toast.error("Please provide an application name");
          return;
        }

        // Auto-generate application_ref_name from application_name
        const generatedRefName = newApplicationName
          .replace(/[^a-zA-Z0-9]/g, "_")
          .replace(/_+/g, "_")
          .replace(/^_|_$/g, "");

        console.log("🏭 CREATING NEW APPLICATION:", { newApplicationName, generatedRefName, companyId });

        toast.info("Creating new application...");
        
        const applicationData = {
          application_name: newApplicationName,
          application_ref_name: generatedRefName,
          company_id: companyId
        };

        const createAppResponse: any = await firstValueFrom(
          ApplicationService.registerApplication(applicationData, null, apiKey)
        );
        console.log("✅ APPLICATION CREATED:", createAppResponse);

        applicationRefName = generatedRefName;
        applicationId = createAppResponse?.id;
        
        console.log("📝 APPLICATION CREATED - ID:", applicationId, "RefName:", applicationRefName);
        
        if (!applicationId) {
          throw new Error("Failed to get application ID from creation response");
        }
        
        toast.success("Application created successfully!");

      } else {
        const selectedApp = applications.find(app => app.id === selectedApplicationId);
        if (!selectedApp) {
          toast.error("Please select an application");
          return;
        }
        applicationRefName = selectedApp.application_ref_name || selectedApp.application_name;
        applicationId = selectedApp.id;
        
        console.log("📝 APPLICATION SELECTED - ID:", applicationId, "RefName:", applicationRefName, "Full App:", selectedApp);
      }

      // Step 2: Handle intent creation or selection  
      if (createNewIntent) {
        if (!newIntentName.trim()) {
          toast.error("Please provide an intent name");
          return;
        }

        console.log("🎯 CREATING NEW INTENT:", { newIntentName, applicationId, companyId });

        toast.info("Creating new intent...");

        const intentData = {
          action_type: newIntentName,
          application_id: applicationId,
          company_id: companyId,
          ...(newIntentDescription && newIntentDescription.trim()
            ? { meta: { metadata: { summary: newIntentDescription.trim() } } }
            : {})
        };

        const createIntentResponse: any = await firstValueFrom(
          ApplicationService.createApplicationAction(applicationId, intentData, null, apiKey)
        );
        console.log("✅ INTENT CREATED:", createIntentResponse);

        actionType = newIntentName;
        intentId = createIntentResponse?.id;
        
        console.log("📝 INTENT CREATED - ID:", intentId, "ActionType:", actionType);
        
        if (!intentId) {
          throw new Error("Failed to get intent ID from creation response");
        }
        
        toast.success("Intent created successfully!");

      } else {
        const selectedIntent = intents.find(intent => intent.id === selectedIntentId);
        if (!selectedIntent) {
          toast.error("Please select an intent");
          return;
        }
        actionType = selectedIntent.action_type;
        intentId = selectedIntent.id;
        
        console.log("📝 INTENT SELECTED - ID:", intentId, "ActionType:", actionType, "Full Intent:", selectedIntent);
      }

      // Step 3: VALIDATE NAME - Check for conflicts BEFORE saving
      if (!applicationId || !intentId) {
        throw new Error("Missing application or intent ID");
      }

      console.log("🔍 FRONTEND VALIDATION - Starting validation with:", {
        companyId,
        applicationId,
        intentId,
        conversationFlowName: conversationFlowName.trim(),
        applicationRefName,
        actionType
      });

      toast.info("Validating tree name...");

      try {
        const validationResult = await firstValueFrom(
          ConversationBuilderService.validateConversationFlowName(
            companyId,
            applicationId,
            intentId,
            conversationFlowName.trim(),
            apiKey,
            1 // agent_id - match what's used when saving trees
          )
        );
        console.log("✅ FRONTEND VALIDATION - Success:", validationResult);
        
        console.log("✅ FRONTEND VALIDATION - Passed, proceeding to save");
      } catch (validationError: any) {
        console.log("FRONTEND VALIDATION - Failed:", validationError);
        console.log("FRONTEND VALIDATION ERROR STRUCTURE:", {
          validationError,
          status: validationError?.status,
          errorProperty: validationError?.error,
          message: validationError?.message,
          errorMessage: validationError?.error?.error
        });
        
        // Handle validation failure with comprehensive error extraction
        let errorMessage = "";
        
        // Check for HTTP conflict status first
        if (validationError.status === 409 || validationError.status === 400 || validationError.status === 422) {
          if (validationError.error?.error && typeof validationError.error.error === "string") {
            errorMessage = validationError.error.error;
          } else if (validationError.error?.message && typeof validationError.error.message === "string") {
            errorMessage = validationError.error.message;
          } else if (typeof validationError.error === "string") {
            errorMessage = validationError.error;
          }
        }
        
        // Fallback to general error extraction
        if (!errorMessage) {
          if (validationError.message && typeof validationError.message === "string") {
            errorMessage = validationError.message;
          } else if (typeof validationError === "string") {
            errorMessage = validationError;
          }
        }
        
        console.log("🔍 VALIDATION ERROR MESSAGE EXTRACTED:", errorMessage);
        
        if (errorMessage) {
          if (errorMessage.toLowerCase().includes("already exists")) {
            toast.error(`${errorMessage}`);
          } else {
            toast.error(`Validation failed: ${errorMessage}`);
          }
        } else {
          console.error("COULD NOT EXTRACT VALIDATION ERROR MESSAGE");
          toast.error(`Name validation failed: ${JSON.stringify(validationError?.error || validationError || "Unknown validation error")}`);
        }
        
        console.log("🛑 FRONTEND VALIDATION - Stopping save due to validation failure");
        return; // Stop here - don't proceed to save
      }

      setIsValidating(false);
      setIsSaving(true);

      // Step 4: SAVE - Name is validated, now save the tree
      toast.info("Saving conversation tree...");
      
      const backendMetadata: BackendSaveMetadata = {
        conversation_flow_name: conversationFlowName.trim(),
        company_id: String(companyId),
        application_ref_name: applicationRefName,
        action_type: actionType,
        conversation_flow_version: 1, // Always version 1 for new trees
        is_latest: true // Always true for new trees
      };

      console.log("💾 FINAL SAVE - Calling onSave with metadata:", backendMetadata);
      console.log("💾 FINAL SAVE - Validation passed, IDs confirmed:", { applicationId, intentId });

      await onSave(backendMetadata);
      
      console.log("✅ SAVE COMPLETED SUCCESSFULLY");
      // Success message now handled by parent component after backend confirms success

    } catch (error: any) {
      console.error("SAVE PROCESS ERROR:", error);
      console.error("ERROR STRUCTURE:", {
        error,
        status: error?.status,
        errorProperty: error?.error,
        message: error?.message,
        errorMessage: error?.error?.error,
        errorString: typeof error?.error === "string" ? error.error : "not string"
      });
      
      // Handle specific error types with comprehensive checking
      let errorMessage = "";
      
      // Check for HTTP conflict status (409) or other validation statuses
      if (error?.status === 409 || error?.status === 400 || error?.status === 422) {
        console.log("🚨 DETECTED VALIDATION ERROR STATUS:", error.status);
        
        // Try multiple ways to extract the error message for validation errors
        if (error?.error?.error && typeof error.error.error === "string") {
          errorMessage = error.error.error;
        } else if (error?.error?.message && typeof error.error.message === "string") {
          errorMessage = error.error.message;
        } else if (typeof error?.error === "string") {
          errorMessage = error.error;
        }
      }
      
      // If no validation-specific error found, try general error extraction
      if (!errorMessage) {
        if (error?.message && typeof error.message === "string") {
          errorMessage = error.message;
        } else if (typeof error === "string") {
          errorMessage = error;
        }
      }
      
      console.log("🔍 EXTRACTED ERROR MESSAGE:", errorMessage);
      
      // Show specific error message if we have one
      if (errorMessage) {
        if (errorMessage.toLowerCase().includes("already exists")) {
          toast.error(`${errorMessage}`);
        } else if (error?.status === 409) {
          toast.error(`${errorMessage}`);
        } else {
          toast.error(`Save failed: ${errorMessage}`);
        }
      } else {
        // If we can't extract a message, show the whole error for debugging
        console.error("COULD NOT EXTRACT ERROR MESSAGE, showing generic");
        toast.error(`Failed to save conversation tree: ${JSON.stringify(error?.error || error || "Unknown error")}`);
      }
    } finally {
      setIsValidating(false);
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    onCancel();
    setConversationFlowName("");
    setSelectedApplicationId(undefined);
    setSelectedIntentId(undefined);
    setIntents([]);
    setCreateNewApplication(false);
    setNewApplicationName("");
    setCreateNewIntent(false);
    setNewIntentName("");
    setNewIntentDescription("");
  };

  const isOkDisabled = () => {
    if (!conversationFlowName.trim()) return true;
    
    if (createNewApplication) {
      if (!newApplicationName.trim()) return true;
      // When creating new application, must also create new intent
      if (!createNewIntent) return true;
    } else {
      if (!selectedApplicationId) return true;
    }
    
    if (createNewIntent) {
      if (!newIntentName.trim()) return true;
    } else {
      if (!selectedIntentId) return true;
    }
    
    return false;
  };

  return (
    <Dialog open={visible} onOpenChange={(open) => { if (!open) handleCancel(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Save Conversation Tree</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Tree Name */}
          <div className="space-y-2">
            <Label htmlFor="treeName" className="text-sm font-gc-med text-foreground">
              Tree Name
            </Label>
            <Input
              id="treeName"
              value={conversationFlowName}
              onChange={(e) => setConversationFlowName(e.target.value)}
              placeholder="Enter conversation tree name"
            />
          </div>

          {/* Application Section */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="application" className="text-sm font-gc-med text-foreground">
                Application
              </Label>
              <div className="flex items-center space-x-2">
                <Switch
                  checked={createNewApplication}
                  onCheckedChange={(checked) => {
                    setCreateNewApplication(checked);
                    setCreateNewIntent(checked); // Auto-select create new intent when creating new application
                    if (checked) {
                      setSelectedApplicationId(undefined);
                      setIntents([]);
                      setSelectedIntentId(undefined);
                    }
                  }}
                />
                <Label className="text-xs text-muted-foreground">Create New</Label>
              </div>
            </div>

            {createNewApplication ? (
              <Input
                value={newApplicationName}
                onChange={(e) => setNewApplicationName(e.target.value)}
                placeholder="Application name"
              />
            ) : (
              <Select value={selectedApplicationId?.toString()} onValueChange={handleApplicationChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select an application" />
                </SelectTrigger>
                <SelectContent>
                  {applications.map((app) => (
                    <SelectItem key={app.id} value={app.id.toString()}>
                      {app.application_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Intent Section */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="intent" className="text-sm font-gc-med text-foreground">
                Intent
              </Label>
              {!createNewApplication && (
                <div className="flex items-center space-x-2">
                  <Switch
                    checked={createNewIntent}
                    onCheckedChange={(checked) => {
                      setCreateNewIntent(checked);
                      if (checked) {
                        setSelectedIntentId(undefined);
                      }
                    }}
                  />
                  <Label className="text-xs text-muted-foreground">
                    Create New
                  </Label>
                </div>
              )}
            </div>

            {createNewIntent ? (
              <div className="space-y-2">
                <Input
                  value={newIntentName}
                  onChange={(e) => setNewIntentName(e.target.value)}
                  placeholder="Intent name (e.g., customer_support, booking)"
                />
                <Input
                  value={newIntentDescription}
                  onChange={(e) => setNewIntentDescription(e.target.value)}
                  placeholder="Description (optional)"
                />
              </div>
            ) : (
              <Select 
                value={selectedIntentId?.toString()} 
                onValueChange={(value) => setSelectedIntentId(parseInt(value))}
                disabled={createNewApplication || !selectedApplicationId}
              >
                <SelectTrigger>
                  <SelectValue 
                    placeholder={
                      createNewApplication 
                        ? "Create application first" 
                        : !selectedApplicationId 
                        ? "Select an application first" 
                        : loadingIntents 
                        ? "Loading intents..." 
                        : "Select an intent"
                    } 
                  />
                </SelectTrigger>
                <SelectContent>
                  {intents.map((intent) => (
                    <SelectItem key={intent.id} value={intent.id.toString()}>
                      <div>
                        <span>{intent.action_type}</span>
                        {intent.description && (
                          <span className="text-xs text-muted-foreground ml-2">
                            - {intent.description}
                          </span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button 
            onClick={handleOk} 
            disabled={isOkDisabled() || isValidating || isSaving}
          >
            {isValidating ? "Validating..." : isSaving ? "Saving..." : "Save Tree"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SaveTreeModal; 
